import { useState, useEffect, useCallback, useMemo } from 'react';
import { FilterBtnFragment, ContentBlockFragment } from '@graphqlTypes/sitecore';
import { OrderContentBlock } from '@src/common/filter';

interface UseFilterStateProps {
    filterButtons: FilterBtnFragment[];
    contentBlocks: ContentBlockFragment[];
    displayArticles: ContentBlockFragment[];
}

export function useFilterState({
    filterButtons,
    contentBlocks,
    displayArticles,
}: UseFilterStateProps) {
    const [selectedFilter, setSelectedFilter] = useState<FilterBtnFragment | 'all'>('all');
    const [filteredList, setFilteredList] = useState<ContentBlockFragment[]>(contentBlocks);

    const displayArticlesList = useMemo(() => {
        return OrderContentBlock(contentBlocks, displayArticles);
    }, [contentBlocks, displayArticles]);

    useEffect(() => {
        const saved = sessionStorage.getItem('selectedFilter');

        if (saved && saved !== 'all') {
            const btn = filterButtons.find((b) => b.id === saved);
            if (btn) {
                setSelectedFilter(btn);
                const targetIds = btn.category?.targetItems?.map((i) => i.id) ?? [];
                const filtered = displayArticlesList.filter((item) =>
                    item.category?.categories?.some((cat) => targetIds.includes(cat.id))
                );
                setFilteredList(filtered);
                return;
            }
        }

        setSelectedFilter('all');
        setFilteredList(displayArticlesList);
    }, [filterButtons, displayArticlesList]);

    const handleFilterClick = useCallback(
        (btn: FilterBtnFragment | 'all') => {
            const next =
                btn === 'all'
                    ? 'all'
                    : selectedFilter !== 'all' && selectedFilter.id === btn.id
                    ? 'all'
                    : btn;

            setSelectedFilter(next);
            sessionStorage.setItem('selectedFilter', next === 'all' ? 'all' : next.id || 'all');

            if (next === 'all') {
                setFilteredList(displayArticlesList);
            } else {
                const targetIds = next.category?.targetItems?.map((i) => i.id) ?? [];
                const filtered = displayArticlesList.filter((item) =>
                    item.category?.categories?.some((cat) => targetIds.includes(cat.id))
                );
                setFilteredList(filtered);
            }
        },
        [selectedFilter, displayArticlesList]
    );

    return {
        selectedFilter,
        filteredList,
        handleFilterClick,
        displayArticles,
    };
}
